// services/searchService.js
import { ACCOUNT_TYPES } from '../config/constants';

/**
 * Build OData filter for advanced search with multi-select logic
 * OR within each field, AND between fields
 *
 * @param {Object} criteria - Selected search criteria
 * @param {Array} criteria.industries - Selected industrycode values
 * @param {Array} criteria.countries - Selected _dimsi_countryid_value values
 * @param {Array} criteria.zones - Selected _dimsi_zoneid_value values
 * @param {Array} criteria.healthScores - Selected dimsi_healthscore codes
 * @param {Array} criteria.partners - Selected dimsi_PartnerId values
 * @returns {string} OData filter string ready for query
 */
export const buildAdvancedSearchFilter = (criteria) => {
  const filterParts = [];

  // Base filter: always signed accounts
  filterParts.push(`parentcustomerid_account/dimsi_typecode eq ${ACCOUNT_TYPES.SIGNED}`);

  // Industries: OR logic (industrycode eq 10 or industrycode eq 20)
  if (criteria.industries && criteria.industries.length > 0) {
    const industryFilters = criteria.industries
      .map(code => `parentcustomerid_account/industrycode eq ${code}`)
      .join(' or ');
    filterParts.push(`(${industryFilters})`);
  }

  // Countries: OR logic (_dimsi_countryid_value eq 'ID1' or _dimsi_countryid_value eq 'ID2')
  if (criteria.countries && criteria.countries.length > 0) {
    const countryFilters = criteria.countries
      .map(id => `parentcustomerid_account/_dimsi_countryid_value eq '${id}'`)
      .join(' or ');
    filterParts.push(`(${countryFilters})`);
  }

  // Zones: OR logic (_dimsi_zoneid_value eq 'ID1' or _dimsi_zoneid_value eq 'ID2')
  if (criteria.zones && criteria.zones.length > 0) {
    const zoneFilters = criteria.zones
      .map(id => `parentcustomerid_account/_dimsi_zoneid_value eq '${id}'`)
      .join(' or ');
    filterParts.push(`(${zoneFilters})`);
  }

  // Health Scores: OR logic (dimsi_healthscore eq 677280000 or dimsi_healthscore eq 677280001)
  if (criteria.healthScores && criteria.healthScores.length > 0) {
    const healthScoreFilters = criteria.healthScores
      .map(code => `parentcustomerid_account/dimsi_healthscore eq ${code}`)
      .join(' or ');
    filterParts.push(`(${healthScoreFilters})`);
  }

  // Partners: OR logic (_dimsi_partnerid_value eq 'ID1' or _dimsi_partnerid_value eq 'ID2')
  if (criteria.partners && criteria.partners.length > 0) {
    const partnerFilters = criteria.partners
      .map(id => `parentcustomerid_account/_dimsi_partnerid_value eq '${id}'`)
      .join(' or ');
    filterParts.push(`(${partnerFilters})`);
  }

  // Project Types: MRR logic > 0
  if (criteria.projectTypes && criteria.projectTypes.length > 0) {
    const projectFilters = criteria.projectTypes
      .map(type => {
        if (type === 'intranet') return 'parentcustomerid_account/dimsi_contractmrrpintranet gt 0';
        if (type === 'teams') return 'parentcustomerid_account/dimsi_contractmrrpteams gt 0';
        if (type === 'manager') return 'parentcustomerid_account/dimsi_contractmrrpmanager gt 0';
        return null;
      })
      .filter(Boolean)
      .join(' or ');
    if (projectFilters) {
      filterParts.push(`(${projectFilters})`);
    }
  }

  // Combine with AND between all filter parts
  return filterParts.join(' and ');
};

/**
 * Transform API response to filter criteria format
 *
 * @param {Object} formData - Form data from SearchForm
 * @returns {Object} Criteria object for buildAdvancedSearchFilter
 */
export const transformFormDataToCriteria = (formData) => {
  return {
    industries: formData.industries || [],
    countries: formData.countries || [],
    zones: formData.zones || [],
    healthScores: formData.healthScores || [],
    partners: formData.partners || [],
    projectTypes: formData.projectTypes || [],
  };
};

/**
 * Filter a list of contacts to keep only one per account.
 * Prioritizes the contact with the most filled criteria.
 *
 * @param {Array} newContacts - List of imported contacts
 * @returns {Array} Unique list of contacts per account
 */
export const getUniqueContactsForSelector = (newContacts) => {
  const countFilledCriteria = (contact) => {
    const account = contact.parentcustomerid_account;
    if (!account) return 0;
    let count = 0;
    if (account.industrycode) count++;
    if (account['_dimsi_countryid_value']) count++;
    if (account['_dimsi_zoneid_value']) count++;
    if (contact.dimsi_nbofemployees) count++;
    return count;
  };

  const grouped = newContacts.reduce((acc, contact) => {
    const accountId = contact.parentcustomerid_account?.accountid;
    if (!accountId) return acc;
    if (!acc[accountId]) acc[accountId] = [];
    acc[accountId].push(contact);
    return acc;
  }, {});

  return Object.values(grouped).map(group => {
    return group.reduce((best, current) => {
      return countFilledCriteria(current) > countFilledCriteria(best) ? current : best;
    });
  });
};
